import React, { useState, useEffect, useRef, useCallback } from "react";
import '../../styles/components/resumeTailoring/resume.scss';
import {
  ResumeText,
  BulletPointSuggestion,
  Education,
  Experience,
} from "./ResumeComponent";
import BulletPoint from "./BulletPoint";
import aiIcon from '../../assets/ai-icon.svg';

// Custom hook for debounced field updates with proper cleanup
const useDebounceCallback = <T extends any[]>(
  callback: (...args: T) => void,
  delay: number
) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return useCallback((...args: T) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => callback(...args), delay);
  }, [callback, delay]);
};

// Example placeholder text for different resume sections
const placeholderExamples = {
  name: "Full Name",
  email: "Email",
  phone: "Phone",
  linkedin: "LinkedIn",
  website: "Website",
  location: "Location",
  jobTitle: "Job Title",
  company: "Company Name",
  dates: "Jun 2022 - Present",
  projectTitle: "Project Title",
  projectName: "- Project Name",
  projectDates: "Jan 2023 - Mar 2023",
  bulletPoint: "Add a bullet point",
  education: "University Name",
  degree: "Degree",
  gpa: "GPA: 3.85",
  graduationDate: "May 2022",
  skill: "Skills (separated by commas)"
};

/** --------------------------------------------------------------------
 * Date Formatting Functions
 * -------------------------------------------------------------------- */
// Month name standardization
const monthAbbreviations: Record<string, string> = {
  "jan": "Jan", "january": "Jan",
  "feb": "Feb", "february": "Feb",
  "mar": "Mar", "march": "Mar",
  "apr": "Apr", "april": "Apr",
  "may": "May",
  "jun": "Jun", "june": "Jun",
  "jul": "Jul", "july": "Jul",
  "aug": "Aug", "august": "Aug",
  "sep": "Sep", "sept": "Sep", "september": "Sep",
  "oct": "Oct", "october": "Oct",
  "nov": "Nov", "november": "Nov",
  "dec": "Dec", "december": "Dec"
};

// Words that should be converted to "Present" for end dates
const presentAlternatives = ["current", "now", "today", "ongoing", "active", "presen", "present"];

// Helper to normalize present alternatives to proper "Present"
const normalizePresentWord = (word: string): string => {
  if (!word) return word;
  
  const lowerWord = word.toLowerCase().trim();
  
  // Check if it's any variation of "present"
  if (presentAlternatives.includes(lowerWord)) {
    return "Present";
  }
  
  // Also handle partial typing or typos of "present"
  if (lowerWord.length >= 3) {
    // Check for common "present" typos
    const presentTypos = [
      'presen', 'present', 'presnt', 'presnet', 'presetn',
      'curren', 'current', 'currnt', 'curent',
      'ongoin', 'ongoing', 'ongoig', 'ongong',
      'activ', 'active', 'activs', 'actve'
    ];
    
    for (const typo of presentTypos) {
      if (lowerWord.startsWith(typo.substring(0, 3)) && 
          calculateEditDistance(lowerWord, typo) <= 2) {
        return "Present";
      }
    }
  }
  
  return word; // Return original if not a present alternative
};

// Helper to calculate edit distance between two strings (for fuzzy matching)
const calculateEditDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // deletion
        matrix[j - 1][i] + 1,     // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
};

// Memoized month variants for performance
const monthVariants = Object.keys(monthAbbreviations);

// Helper to auto-correct misspelled month names (optimized)
const correctMisspelledMonth = (monthInput: string): string => {
  if (!monthInput) return monthInput;
  
  const input = monthInput.toLowerCase().trim();
  
  // If it's already a known month, return the correct abbreviation
  if (monthAbbreviations[input]) {
    return monthAbbreviations[input];
  }
  
  // Quick prefix check first (most common case)
  const monthPrefixes = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  for (const prefix of monthPrefixes) {
    if (input.startsWith(prefix)) {
      return monthAbbreviations[prefix === 'sep' ? 'september' : prefix === 'may' ? 'may' : 
             prefix === 'jun' ? 'june' : prefix === 'jul' ? 'july' :
             `${prefix}uary`.includes('uary') ? `${prefix}uary` : `${prefix}ember`.includes('ember') ? `${prefix}ember` : 
             prefix + (prefix.length === 3 ? 'ust' : 'il')] || monthAbbreviations[prefix];
    }
  }
  
  // Fallback to detailed matching for edge cases
  let bestMatch = monthInput;
  let bestScore = 0;
  
  for (const monthVariant of monthVariants) {
    const distance = calculateEditDistance(input, monthVariant);
    const commonChars = input.split('').filter(char => monthVariant.includes(char)).length;
    const commonScore = commonChars / Math.max(input.length, monthVariant.length);
    
    let score = 0;
    if (commonScore > 0.6) score += 30;
    if (distance <= 2) score += (3 - distance) * 20;
    if (input.startsWith(monthVariant.substring(0, 3))) score += 40;
    
    if (score > bestScore && score >= 25) {
      bestScore = score;
      bestMatch = monthAbbreviations[monthVariant];
    }
  }
  
  return bestScore >= 25 ? bestMatch : monthInput;
};

// Helper to check if a string looks like a valid date format before trying to standardize it
const isValidDateFormat = (dateString: string): boolean => {
  if (!dateString || dateString.trim().length === 0) return false;
  
  const trimmed = dateString.trim();
  
  // Check for common date patterns:
  // - Month Year (e.g., "Jan 2022", "January 2022")
  // - Year only (e.g., "2022")
  // - Month/Year variants (e.g., "Jan2022", "01/2022")
  
  const datePatterns = [
    /^[a-zA-Z]{3,9}\s+\d{4}$/,        // "Jan 2022", "January 2022"
    /^[a-zA-Z]{3,9}\d{4}$/,           // "Jan2022", "January2022" 
    /^\d{4}$/,                        // "2022"
    /^\d{1,2}\/\d{4}$/,               // "01/2022"
    /^[a-zA-Z]{3,9}\s+\d{2}$/,        // "Jan 22"
  ];
  
  // Check if it matches any common date pattern
  for (const pattern of datePatterns) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }
  
  // Also check if the first word is a recognizable month (even if misspelled)
  const words = trimmed.split(/\s+/);
  if (words.length > 0) {
    const firstWord = words[0].toLowerCase();
    
    // Check if it starts with common month prefixes (more aggressive)
    const monthPrefixes = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    for (const prefix of monthPrefixes) {
      if (firstWord.startsWith(prefix) || prefix.startsWith(firstWord.substring(0, 3))) {
        return true;
      }
    }
    
    // Check if it's close to a month name (allowing for typos)
    const monthNames = Object.keys(monthAbbreviations);
    for (const month of monthNames) {
      if (firstWord === month || firstWord.startsWith(month.substring(0, 3))) {
        return true;
      }
    }
    
    // Also check using our spell correction - if it can be corrected, it's probably a date
    const corrected = correctMisspelledMonth(firstWord);
    if (corrected !== firstWord) {
      return true;
    }
  }
  
  return false;
};

// Smart date formatter function - much more conservative approach
const formatDateString = (dateText: string): { startDate: string, endDate: string | null, isOngoing: boolean } => {
  // Default return values
  const result = {
    startDate: "",
    endDate: null as string | null,
    isOngoing: false
  };
  
  if (!dateText) return result;
  
  const trimmedText = dateText.trim();
  
  // Handle case where user just typed "present" or similar without a date range
  const normalizedWord = normalizePresentWord(trimmedText);
  if (normalizedWord === "Present") {
    result.startDate = "Present";
    result.endDate = null;
    result.isOngoing = true;
    return result;
  }
  
  // Only try to parse if it looks like a date range (contains separators)
  const separatorRegex = /\s*[-–—~]\s*|\s+to\s+/i;
  if (!separatorRegex.test(trimmedText)) {
    // No separator found - treat as single date/text
    result.startDate = trimmedText;
    return result;
  }
  
  // Split by common separators (-, to, ~, etc.)
  const dateComponents = trimmedText.split(separatorRegex);
  
  // Process start date - only standardize if it looks like a valid date
  if (dateComponents.length > 0 && dateComponents[0].trim()) {
    const startPart = dateComponents[0].trim();
    result.startDate = isValidDateFormat(startPart) ? standardizeDate(startPart) : startPart;
  }
  
  // Process end date
  if (dateComponents.length > 1 && dateComponents[1].trim()) {
    const endPart = dateComponents[1].trim();
    
    // Check if it's a "Present" equivalent and normalize it
    const normalizedEndDate = normalizePresentWord(endPart);
    if (normalizedEndDate === "Present") {
      result.endDate = null;
      result.isOngoing = true;
    } else {
      // Only standardize if it looks like a valid date
      result.endDate = isValidDateFormat(endPart) ? standardizeDate(endPart) : endPart;
      result.isOngoing = false;
    }
  }
  
  return result;
};

// Helper to standardize individual date parts - more conservative approach with auto-correction
const standardizeDate = (dateString: string): string => {
  // Handle empty case
  if (!dateString) return "";
  
  const trimmed = dateString.trim();
  
  // If it doesn't look like a date, don't mess with it
  if (!isValidDateFormat(trimmed)) {
    return trimmed;
  }
  
  // Handle cases like "Jan2022" or "Augsye2022" (no space)
  const noSpaceMatch = trimmed.match(/^([a-zA-Z]{3,9})(\d{4})$/);
  if (noSpaceMatch) {
    const monthPart = noSpaceMatch[1];
    const yearPart = noSpaceMatch[2];
    
    // Try to correct the month spelling
    const correctedMonth = correctMisspelledMonth(monthPart);
    return `${correctedMonth} ${yearPart}`;
  }
  
  // Split the date into components (usually Month Year)
  const parts = trimmed.split(/\s+/);
  
  // Check if we have enough parts to work with
  if (parts.length < 1) return trimmed;
  
  // Try to standardize/correct the month
  const monthCandidate = parts[0];
  const correctedMonth = correctMisspelledMonth(monthCandidate);
  
  // Use the corrected month
  parts[0] = correctedMonth;
  
  // Reconstruct the standardized date
  return parts.join(" ");
};

// Format graduation date - adds "Expected" for future dates
const formatGraduationDate = (dateText: string): string => {
  if (!dateText) return "";
  
  // First remove any existing "Expected" prefix to avoid duplication
  const cleanDateText = dateText.replace(/^expected\s+/i, '');
  
  // Parse the date to determine if it's in the future
  const datePattern = /([a-z]+)?\s*(\d{4})/i;
  const match = cleanDateText.match(datePattern);
  
  if (match) {
    // Extract month and year
    const month = match[1];
    const year = parseInt(match[2], 10);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // Create a date object for comparison (default to January if no month specified)
    const monthIndex = month ? new Date(`${month} 1, 2000`).getMonth() : 0;
    const graduationDate = new Date(year, monthIndex);
    
    // Compare with current date
    if (year > currentYear || (year === currentYear && monthIndex > currentMonth)) {
      // Future date - add Expected prefix
      return `Expected ${cleanDateText}`;
    }
    
    // Past or current date - just return the cleaned date
    return cleanDateText;
  }
  
  // If we couldn't parse the date, return it as is
  return dateText;
};

const formatDateRange = (startDate: string, endDate: string | null, isCurrentPosition: boolean = false): string => {
  if (!startDate) return "";
  if (isCurrentPosition) return `${startDate} - Present`;
  return endDate ? `${startDate} - ${endDate}` : startDate;
};

const formatProjectDuration = (startDate: string, endDate: string | null, isOngoing: boolean = false): string => {
  if (!startDate) return "";
  if (isOngoing) return `${startDate} - Present`;
  return endDate ? `${startDate} - ${endDate}` : startDate;
};

/** --------------------------------------------------------------------
 * Contact Info Formatting Functions
 * -------------------------------------------------------------------- */
const formatLinkedInUrl = (linkedin: string): string => {
  if (!linkedin) return "";
  // Remove https:// and add www.
  let formatted = linkedin.replace(/^https?:\/\//, "");
  if (!formatted.startsWith("www.")) {
    formatted = `www.${formatted}`;
  }
  return formatted;
};

const formatWebsiteUrl = (website: string): string => {
  if (!website) return "";
  // Remove https:// and add www.
  let formatted = website.replace(/^https?:\/\//, "");
  if (!formatted.startsWith("www.")) {
    formatted = `www.${formatted}`;
  }
  return formatted;
};

const formatLocation = (location: string): string => {
  if (!location) return "";
  // Convert "city.state" to "city, state"
  return location.replace(/\./g, ", ");
};

interface ResumeProps {
  revisedResume: ResumeText;
  handleFieldChange: (updater: (prev: ResumeText) => ResumeText) => void;
  handleTailorBulletPoint: (
    expIndex: number,
    descId: string
  ) => Promise<void>;
  selectedBulletPoint: BulletPointSuggestion | null;
  handleAcceptSuggestion: (expIndex: number, descId: string) => void;
  handleRejectSuggestion: () => void;
  expandedBulletPoint: { expIndex: number, descId: string } | null;
}

const ResumeComponent: React.FC<ResumeProps> = ({
  revisedResume,
  handleFieldChange,
  handleTailorBulletPoint,
  selectedBulletPoint,
  handleAcceptSuggestion,
  handleRejectSuggestion,
  expandedBulletPoint,
}) => {
  const nameFieldRef = useRef<HTMLDivElement>(null);
  // Local state for immediate UI updates (prevents input lag)
  const [localResume, setLocalResume] = useState<ResumeText>(revisedResume);
  // Track which bullet point has text selected (for AI button display and custom highlighting)
  const [activeSelectionPoint, setActiveSelectionPoint] = useState<{
    expIndex: number;
    descId: string;
  } | null>(null);
  const [showAIButton, setShowAIButton] = useState(false);
  const [, setAiButtonPosition] = useState({ top: 0, left: 0 });
  const bulletPointRef = useRef<HTMLDivElement>(null);
  const [suggestionWidth, setSuggestionWidth] = useState<number>(0);
  const [aiButtonWidth, setAiButtonWidth] = useState<number>(0);

  // Get AI context for managing AI button state (commented out since unused)
  // const { setExpandedBulletPoint: setAIExpandedBulletPoint } = useAI();

  // Debounced field change handler (300ms delay for performance)
  const debouncedFieldChange = useDebounceCallback(handleFieldChange, 300);

  // Sync local state when external props change
  useEffect(() => {
    setLocalResume(revisedResume);
  }, [revisedResume]);

  // Optimized field change handler
  const handleOptimizedFieldChange = useCallback((updater: (prev: ResumeText) => ResumeText) => {
    // Immediate local update for responsive UI
    setLocalResume(prev => {
      const updated = updater(prev);
      // Also trigger debounced external update
      debouncedFieldChange(() => updated);
      return updated;
    });
  }, [debouncedFieldChange]);

  // Memoized date formatting removed since not used directly

  // Callback for bullet point text updates
  const handleBulletPointUpdate = useCallback((expIndex: number, descId: string, newText: string) => {
    handleOptimizedFieldChange((prev) => {
      const updatedExperience = [...prev.experience];
      const currentDescription = updatedExperience[expIndex].description.find(
        (d) => d.id === descId
      );

      if (currentDescription) {
        currentDescription.text = newText;
      }
      return { ...prev, experience: updatedExperience };
    });
  }, [handleOptimizedFieldChange]);

  // Project bullet point updates handled inline (removed unused callback)

  // Fix the selectionchange event listener with proper cleanup - no dependencies to prevent re-creation
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      const selectedText = selection?.toString().trim();

      if (!selectedText) {
        setShowAIButton(false);
        setActiveSelectionPoint(null);
        return;
      }

      const range = selection?.getRangeAt(0);
      if (!range) return;

      // Find the bullet point containing the selection
      const bulletPointElement = range.commonAncestorContainer.parentElement?.closest('.bullet-points li');
      if (!bulletPointElement) {
        setShowAIButton(false);
        setActiveSelectionPoint(null);
        return;
      }

      // Get the expIndex and descId from data attributes
      const expIndex = parseInt(bulletPointElement.getAttribute('data-exp-index') || '-1');
      const descId = bulletPointElement.getAttribute('data-desc-id') || '';

      if (expIndex === -1 || !descId) {
        setShowAIButton(false);
        setActiveSelectionPoint(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      const buttonWidth = 160;
      const centerPosition = rect.left + (rect.width * 0.7) - (buttonWidth / 2);

      setAiButtonPosition({
        top: rect.bottom + 5,
        left: centerPosition,
      });
      
      const pointInfo = { expIndex, descId };
      setActiveSelectionPoint(pointInfo);
      setShowAIButton(true);
      // Don't set expandedBulletPoint here - that should only happen when AI processing starts
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []); // Empty dependencies - event handler defined inline to prevent recreation

  // Fix the resize observer with proper cleanup - no dependencies to prevent re-creation
  useEffect(() => {
    const currentRef = bulletPointRef.current;
    if (!currentRef) return;
    
    setSuggestionWidth(currentRef.offsetWidth);
    setAiButtonWidth(currentRef.offsetWidth);

    const handleResizeObservation = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.target instanceof HTMLElement) {
          setSuggestionWidth(entry.target.offsetWidth);
          setAiButtonWidth(entry.target.offsetWidth);
        }
      }
    };

    const resizeObserver = new ResizeObserver(handleResizeObservation);
    resizeObserver.observe(currentRef);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, []); // Empty dependencies - handlers defined inline

  // Fix window resize event listener - no dependencies to prevent re-creation
  useEffect(() => {
    const handleResize = () => {
      if (bulletPointRef.current) {
        setSuggestionWidth(bulletPointRef.current.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependencies - handler defined inline

  // Add effect to initialize empty classes on all editable fields
  useEffect(() => {
    // Find all editable fields and initialize empty classes
    const editableFields = document.querySelectorAll('[contenteditable="true"]');
    editableFields.forEach(field => {
      const content = field.textContent || '';
      if (content.trim() === '') {
        field.classList.add('empty');
      } else {
        field.classList.remove('empty');
      }
    });
  }, [revisedResume]); // Re-run when resume data changes

  // Remove auto-focus from name field

  // Helper function to conditionally add empty class for placeholders
  const getEditableFieldClasses = (isEmpty: boolean, baseClass: string = "") => {
    return `editable-field ${isEmpty ? 'empty' : ''} ${baseClass}`.trim();
  };

  // Handle input events to dynamically update empty class
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const content = e.currentTarget.textContent || '';
    if (content.trim() === '') {
      e.currentTarget.classList.add('empty');
    } else {
      e.currentTarget.classList.remove('empty');
    }
  };

  return (
    <div className="resume-rectangle">
      <div className="resume-header">
        <div
          ref={nameFieldRef}
          contentEditable={true}
          suppressContentEditableWarning
          onBlur={(e) =>
            handleOptimizedFieldChange((prev) => ({
              ...prev,
              firstName: e.currentTarget?.innerText ? e.currentTarget.innerText.split(' ')[0] : prev.firstName,
              lastName: e.currentTarget?.innerText ? e.currentTarget.innerText.split(' ').slice(1).join(' ') : prev.lastName,
            }))
          }
          className={getEditableFieldClasses(!localResume.firstName && !localResume.lastName, "resume-name")}
          data-placeholder={placeholderExamples.name}
        >
          {localResume.firstName} {localResume.lastName}
        </div>
        <div className="contact-info">
          {localResume.email && (
            <>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onBlur={(e) =>
                  handleOptimizedFieldChange((prev) => ({
                    ...prev,
                    email: e.currentTarget?.innerText ?? prev.email,
                  }))
                }
              >
                {localResume.email}
              </div>
              {(localResume.phone || localResume.linkedin || localResume.website || localResume.location || localResume.citizenship) && (
                <span className="divider">|</span>
              )}
            </>
          )}

          {localResume.phone && (
            <>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onBlur={(e) =>
                  handleOptimizedFieldChange((prev) => ({
                    ...prev,
                    phone: e.currentTarget?.innerText ?? prev.phone,
                  }))
                }
              >
                {localResume.phone}
              </div>
              {(localResume.linkedin || localResume.website || localResume.location || localResume.citizenship) && (
                <span className="divider">|</span>
              )}
            </>
          )}

          {localResume.linkedin && (
            <>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onBlur={(e) =>
                  handleOptimizedFieldChange((prev) => ({
                    ...prev,
                    linkedin: e.currentTarget?.innerText ?? prev.linkedin,
                  }))
                }
              >
                {formatLinkedInUrl(localResume.linkedin)}
              </div>
              {(revisedResume.website || revisedResume.location || revisedResume.citizenship) && (
                <span className="divider">|</span>
              )}
            </>
          )}

          {revisedResume.website && (
            <>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onBlur={(e) =>
                  handleFieldChange((prev) => ({
                    ...prev,
                    website: e.currentTarget?.innerText ?? prev.website,
                  }))
                }
              >
                {formatWebsiteUrl(revisedResume.website)}
              </div>
              {(revisedResume.location || revisedResume.citizenship) && (
                <span className="divider">|</span>
              )}
            </>
          )}

          {revisedResume.location && (
            <>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onBlur={(e) =>
                  handleFieldChange((prev) => ({
                    ...prev,
                    location: e.currentTarget?.innerText ?? prev.location,
                  }))
                }
              >
                {formatLocation(revisedResume.location)}
              </div>
              {(revisedResume.citizenship) && (
                <span className="divider">|</span>
              )}
            </>
          )}

          {revisedResume.citizenship && (
            <div
              contentEditable={true}
              suppressContentEditableWarning
              onBlur={(e) =>
                handleFieldChange((prev) => ({
                  ...prev,
                  citizenship: e.currentTarget?.innerText ?? prev.citizenship,
                }))
              }
            >
              {revisedResume.citizenship}
            </div>
          )}
        </div>
      </div>

      <div className="education-section">
        <h2 className="resume-section-title">EDUCATION</h2>
        {localResume.education.map((edu: Education, index: number) => (
          <div key={index} className="education-item">
            <div className="education-header">
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onBlur={(e) =>
                  handleFieldChange((prev) => {
                    const updatedEducation = [...prev.education];
                    updatedEducation[index].university = e.currentTarget?.innerText ?? edu.university;
                    return { ...prev, education: updatedEducation };
                  })
                }
                className={getEditableFieldClasses(!edu.university, "education-title")}
                data-placeholder={placeholderExamples.education}
              >
                {edu.university}
              </div>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={(e) =>
                  handleFieldChange((prev) => {
                    const updatedEducation = [...prev.education];
                    // Handle both formats (with or without "GPA:" prefix)
                    const gpaText = e.currentTarget?.innerText?.replace(/^GPA:\s*/i, '') ?? edu.gpa;
                    updatedEducation[index].gpa = gpaText;
                    return { ...prev, education: updatedEducation };
                  })
                }
                className={getEditableFieldClasses(!edu.gpa, "gpa right-aligned")}
                data-placeholder={placeholderExamples.gpa}
              >
                {edu.gpa && `GPA: ${edu.gpa}`}
              </div>
            </div>
            <div className="education-subheader">
              <div className="degree-field">
                <span
                  contentEditable={true}
                  suppressContentEditableWarning
                  onInput={handleInput}
                  onBlur={(e) =>
                    handleFieldChange((prev) => {
                      const updatedEducation = [...prev.education];
                      updatedEducation[index].degree = e.currentTarget?.innerText ?? edu.degree;
                      return { ...prev, education: updatedEducation };
                    })
                  }
                  className={getEditableFieldClasses(!edu.degree, "")}
                  data-placeholder={placeholderExamples.degree}
                >
                  {edu.degree}
                </span>
                {edu.field && (
                  <span
                    contentEditable={true}
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleFieldChange((prev) => {
                        const updatedEducation = [...prev.education];
                        updatedEducation[index].field = e.currentTarget?.innerText ?? edu.field;
                        return { ...prev, education: updatedEducation };
                      })
                    }
                  >
                    , {edu.field}
                  </span>
                )}
              </div>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={(e) =>
                  handleFieldChange((prev) => {
                    const updatedEducation = [...prev.education];
                    const inputText = e.currentTarget?.innerText ?? edu.graduationDate;
                    updatedEducation[index].graduationDate = inputText;
                    
                    // Format graduation date for display (adds Expected if future date)
                    const formattedDate = formatGraduationDate(inputText);
                    if (formattedDate !== inputText) {
                      e.currentTarget.innerText = formattedDate;
                    }
                    
                    return { ...prev, education: updatedEducation };
                  })
                }
                className={getEditableFieldClasses(!edu.graduationDate, "graduation-date right-aligned")}
                data-placeholder={placeholderExamples.dates}
              >
                {formatGraduationDate(edu.graduationDate)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="experience-section">
        <h2 className="resume-section-title">EXPERIENCE</h2>
        {localResume.experience.map((exp: Experience, expIndex: number) => (
          <div key={expIndex} className="experience-item">
            <div className="experience-header">
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onBlur={(e) =>
                  handleFieldChange((prev) => {
                    const updatedExperience = [...prev.experience];
                    updatedExperience[expIndex].title = e.currentTarget?.innerText ?? exp.title;
                    return { ...prev, experience: updatedExperience };
                  })
                }
                className={getEditableFieldClasses(!exp.title, "job-title")}
                data-placeholder={placeholderExamples.jobTitle}
              >
                {exp.title}
              </div>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={(e) =>
                  handleFieldChange((prev) => {
                    const updatedExperience = [...prev.experience];
                    const dateText = e.currentTarget?.innerText?.trim();
                    if (!dateText) {
                      // Handle empty case by adding empty class back
                      e.currentTarget.classList.add('empty');
                      return prev; // Keep previous state if null
                    }
                    
                    // Don't format if user is still typing or if it doesn't look like a date
                    const hasDateSeparator = /\s*[-–—~]\s*|\s+to\s+/i.test(dateText);
                    const normalizedPresent = normalizePresentWord(dateText);
                    const isPresentWord = normalizedPresent === "Present";
                    
                    if (!hasDateSeparator && !isPresentWord && !isValidDateFormat(dateText)) {
                      // Doesn't look like a date format we should mess with
                      updatedExperience[expIndex].startDate = dateText;
                      updatedExperience[expIndex].endDate = null;
                      updatedExperience[expIndex].isCurrentPosition = false;
                      return { ...prev, experience: updatedExperience };
                    }
                    
                    // Use our smart formatter only for valid date input
                    const { startDate, endDate, isOngoing } = formatDateString(dateText);
                    
                    // Update with formatted values
                    updatedExperience[expIndex].startDate = startDate;
                    updatedExperience[expIndex].endDate = endDate;
                    updatedExperience[expIndex].isCurrentPosition = isOngoing;
                    
                    // Only update display if formatting actually improved something
                    const formattedDate = formatDateRange(startDate, endDate, isOngoing);
                    if (formattedDate !== dateText && (hasDateSeparator || isPresentWord)) {
                      e.currentTarget.innerText = formattedDate;
                    }
                    
                    return { ...prev, experience: updatedExperience };
                  })
                }
                className={getEditableFieldClasses(
                  !formatDateRange(exp.startDate, exp.endDate, exp.isCurrentPosition),
                  "duration right-aligned"
                )}
                data-placeholder={placeholderExamples.dates}
              >
                {formatDateRange(exp.startDate, exp.endDate, exp.isCurrentPosition)}
              </div>
            </div>
            <div className="experience-subheader">
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={(e) =>
                  handleFieldChange((prev) => {
                    const updatedExperience = [...prev.experience];
                    updatedExperience[expIndex].company = e.currentTarget?.innerText ?? exp.company;
                    return { ...prev, experience: updatedExperience };
                  })
                }
                className={getEditableFieldClasses(!exp.company, "company-name")}
                data-placeholder={placeholderExamples.company}
              >
                {exp.company}
              </div>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={(e) =>
                  handleFieldChange((prev) => {
                    const updatedExperience = [...prev.experience];
                    updatedExperience[expIndex].location = e.currentTarget?.innerText ?? exp.location;
                    return { ...prev, experience: updatedExperience };
                  })
                }
                className={getEditableFieldClasses(!exp.location, "location right-aligned")}
                data-placeholder={placeholderExamples.location}
              >
                {exp.location}
              </div>
            </div>
            <ul className="bullet-points">
              {exp.description.map((desc: { id: string; text: string }, descIndex: number) => (
                                  <BulletPoint
                    key={desc.id}
                    desc={desc}
                    expIndex={expIndex}
                    isEditingLocked={false}
                    isFirstBulletPoint={expIndex === 0 && descIndex === 0}
                    onUpdateText={(descId, newText) => handleBulletPointUpdate(expIndex, descId, newText)}
                  onTailorBulletPoint={handleTailorBulletPoint}
                  onAcceptSuggestion={handleAcceptSuggestion}
                  placeholderText={placeholderExamples.bulletPoint}
                  showAIButton={activeSelectionPoint?.expIndex === expIndex && activeSelectionPoint?.descId === desc.id && showAIButton}
                  aiButtonWidth={aiButtonWidth}
                  suggestionWidth={suggestionWidth}
                  getEditableFieldClasses={getEditableFieldClasses}
                  onClearSelection={() => {
                    setShowAIButton(false);
                    setActiveSelectionPoint(null);
                  }}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="projects-section">
        <h2 className="resume-section-title">PROJECTS</h2>
        {localResume.projects.map((project, index) => (
          <div key={index} className="project-item">
            <div className="project-header">
              <div className="project-title-container">
                <div
                  contentEditable={true}
                  suppressContentEditableWarning
                  onBlur={(e) =>
                    handleFieldChange((prev) => {
                      const updatedProjects = [...prev.projects];
                      updatedProjects[index].title = e.currentTarget?.innerText ?? project.title;
                      return { ...prev, projects: updatedProjects };
                    })
                  }
                  className={getEditableFieldClasses(!project.title, "project-title")}
                  data-placeholder={placeholderExamples.projectTitle}
                >
                  {project.title}
                </div>
                <div
                  contentEditable={true}
                  suppressContentEditableWarning
                  onInput={handleInput}
                  onBlur={(e) =>
                    handleFieldChange((prev) => {
                      const updatedProjects = [...prev.projects];
                      let inputText = e.currentTarget?.innerText ?? project.project_name;
                      
                      // Only add dash if there's actual text and it doesn't already have a dash
                      if (inputText && inputText.trim() !== "" && !inputText.trim().startsWith('-') && !inputText.trim().startsWith('—')) {
                        inputText = `- ${inputText.trim()}`;
                        // Update the display with the dash
                        e.currentTarget.innerText = inputText;
                      }
                      
                      // If the field is totally empty, make sure we don't keep any dashes
                      if (!inputText || inputText.trim() === "" || inputText.trim() === "-") {
                        inputText = "";
                        e.currentTarget.innerText = "";
                        // Add the empty class back
                        e.currentTarget.classList.add('empty');
                        // Reset any leftover spacing that might have been applied
                        e.currentTarget.style.margin = "0";
                        e.currentTarget.style.padding = "0 0 0 4px"; // Keep only the left padding
                      }
                      
                      updatedProjects[index].project_name = inputText;
                      return { ...prev, projects: updatedProjects };
                    })
                  }
                  className={getEditableFieldClasses(!project.project_name, "project-name")}
                  data-placeholder={placeholderExamples.projectName}
                >
                  {project.project_name}
                </div>
              </div>
              <div
                contentEditable={true}
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={(e) =>
                  handleOptimizedFieldChange((prev) => {
                    const updatedProjects = [...prev.projects];
                    const dateText = e.currentTarget?.innerText?.trim();
                    if (!dateText) {
                      // Handle empty case by adding empty class back
                      e.currentTarget.classList.add('empty');
                      return prev; // Keep previous state if null
                    }
                    
                    // Use the same improved logic as experience section
                    const hasDateSeparator = /\s*[-–—~]\s*|\s+to\s+/i.test(dateText);
                    const normalizedPresent = normalizePresentWord(dateText);
                    const isPresentWord = normalizedPresent === "Present";
                    
                    if (!hasDateSeparator && !isPresentWord && !isValidDateFormat(dateText)) {
                      // Doesn't look like a date format we should mess with
                      updatedProjects[index].startDate = dateText;
                      updatedProjects[index].endDate = null;
                      updatedProjects[index].isOngoing = false;
                      return { ...prev, projects: updatedProjects };
                    }
                    
                    // Use our improved smart formatter
                    const { startDate, endDate, isOngoing } = formatDateString(dateText);
                    
                    // Update with formatted values
                    updatedProjects[index].startDate = startDate;
                    updatedProjects[index].endDate = endDate;
                    updatedProjects[index].isOngoing = isOngoing;
                    
                    // Only update display if formatting actually improved something
                    const formattedDate = formatProjectDuration(startDate, endDate, isOngoing);
                    if (formattedDate !== dateText && (hasDateSeparator || isPresentWord)) {
                      e.currentTarget.innerText = formattedDate;
                    }
                    
                    return { ...prev, projects: updatedProjects };
                  })
                }
                className={
                  getEditableFieldClasses(
                    !formatProjectDuration(project.startDate, project.endDate, project.isOngoing),
                    "project-duration right-aligned"
                  )
                }
                data-placeholder={placeholderExamples.projectDates}
              >
                {formatProjectDuration(project.startDate, project.endDate, project.isOngoing)}
              </div>
            </div>
            <ul className="bullet-points">
              {project.description.map((desc: { id: string; text: string }, descIndex: number) => (
                <li
                  key={desc.id}
                  className={`
                    ${expandedBulletPoint?.expIndex === index && expandedBulletPoint?.descId === desc.id ? "highlighted" : ""} 
                  `}
                  data-exp-index={index}
                  data-desc-id={desc.id}
                >
                  <div
                    contentEditable={true}
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleOptimizedFieldChange((prev) => {
                        const updatedProjects = [...prev.projects];
                        const currentDescription = updatedProjects[index].description.find(
                          (d) => d.id === desc.id
                        );

                        if (currentDescription) {
                          currentDescription.text = e.currentTarget?.innerText ?? desc.text;
                        }
                        return { ...prev, projects: updatedProjects };
                      })
                    }
                    className={getEditableFieldClasses(!desc.text, "project-description")}
                    data-placeholder={placeholderExamples.bulletPoint}
                  >
                    {desc.text}
                  </div>

                    {activeSelectionPoint?.expIndex === index && 
                    activeSelectionPoint?.descId === desc.id &&
                    showAIButton && (
                      <div
                        className={'tailor-ai-btn-container visible'}
                        style={{
                          position: 'absolute',
                          width: aiButtonWidth ? `${aiButtonWidth}px` : 'auto',
                          maxWidth: '100%',
                          zIndex: 1000,
                          left: '80%',           // Now matches the experience section
                          transform: 'translateX(-50%)'
                        }}
                      >
                        <button
                          className="tailor-ai-btn"
                          onClick={() => {
                            handleTailorBulletPoint(index, desc.id);
                            setShowAIButton(false);
                            setActiveSelectionPoint(null);
                          }}
                        >
                          <img 
                            src={aiIcon}
                            alt="AI Icon" 
                            width={20} 
                            height={20}
                            style={{ filter: 'invert(58%) sepia(7%) saturate(98%) hue-rotate(314deg) brightness(89%) contrast(86%)' }}
                          />
                          <span className="btn-text">Tailor with AI</span>
                        </button>
                      </div>
                    )}

                    {selectedBulletPoint &&
                      selectedBulletPoint.expIndex === index &&
                      selectedBulletPoint.descId === desc.id && (
                        <div 
                          className="bullet-point-suggestion" 
                          role="button"
                          style={{ 
                            width: suggestionWidth ? `${suggestionWidth}px` : 'auto',
                            maxWidth: '100%'
                          }}
                        >
                          <div className="suggestion-header">
                            <img 
                              src={aiIcon}
                              alt="AI Icon" 
                              width={20} 
                              height={20}
                              style={{ filter: 'invert(58%) sepia(7%) saturate(98%) hue-rotate(314deg) brightness(89%) contrast(86%)' }}
                            />
                            <span className="suggestion-title">
                              Suggestion
                            </span>
                          </div>
                          
                          {selectedBulletPoint.isLoading ? (
                            <div className="skeleton-lines">
                              <div className="skeleton-loader" style={{ width: '100%' }}></div>
                              <div className="skeleton-loader" style={{ width: '60%' }}></div>
                            </div>
                          ) : (
                            <>
                              {selectedBulletPoint.suggested ? (
                                <div>{selectedBulletPoint.suggested}</div>
                              ) : (
                                ""
                              )}
                              <button
                                className="bullet-point-button"
                                onClick={() => handleAcceptSuggestion(index, desc.id)}
                              >
                                Approve
                              </button>
                              <button onClick={handleRejectSuggestion}>
                                Dismiss
                              </button>
                            </>
                          )}
                        </div>
                      )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="projects-section">  {/* Using same class as other sections */}
        <h2 className="resume-section-title">SKILLS</h2>
        <div className="skills-interests-content">
          <div className="skills-line">
            <span
              contentEditable={true}
              suppressContentEditableWarning
              onBlur={(e) =>
                handleOptimizedFieldChange((prev) => ({
                  ...prev,
                  skills: e.currentTarget?.innerText
                    ? e.currentTarget.innerText
                        .split(",")
                        .map((skill) => skill.trim())
                    : prev.skills,
                }))
              }
              className={getEditableFieldClasses(!localResume.skills || localResume.skills.length === 0, "resume-skills")}
              data-placeholder={placeholderExamples.skill}
            >
              {localResume.skills?.length > 0 ? localResume.skills.join(", ") : ""}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeComponent;